import { useState, useCallback } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { RefreshCw, Pencil, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"

import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { JsonViewer } from "@/components/shared/json-viewer"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { GlassCard } from "@/components/shared/glass-card"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { ShareLinkDialog } from "@/components/shared/share-link-dialog"
import { DestinationForm } from "../components/destination-form"
import { DestinationQuery, DeliveriesQuery, PollDeliveriesQuery } from "@/lib/graphql/queries"
import { RotateDestinationSecretMutation, RegeneratePollApiKeyMutation, AckDeliveriesMutation } from "@/lib/graphql/mutations"
import { Checkbox } from "@/components/ui/checkbox"
import { destinationTypeLabels, type DestinationTypeValue } from "@/lib/schemas"
import type { ResultOf } from "@/lib/graphql/types"

type DestSub = NonNullable<ResultOf<typeof DestinationQuery>["destination"]>["subscriptions"][number]
type DeliveryItem = ResultOf<typeof DeliveriesQuery>["deliveries"]["nodes"][number]
type PollDeliveryItem = ResultOf<typeof PollDeliveriesQuery>["pollDeliveries"]["nodes"][number]

const subColumns: Column<DestSub>[] = [
  {
    header: "Name",
    cell: (row) => <span className="font-medium">{row.name}</span>,
  },
  {
    header: "Source",
    cell: (row) => (
      <Link to={`/sources/${row.source.id}`} className="text-xs hover:underline">
        {row.source.name}
      </Link>
    ),
  },
  {
    header: "Filter",
    cell: (row) => (
      <span className="text-xs text-muted-foreground font-mono">
        {row.filterConfig?.eventTypes?.join(", ") || "*"}
      </span>
    ),
  },
  {
    header: "Status",
    cell: (row) => (
      <Badge variant={row.enabled ? "default" : "secondary"} className="text-2xs">
        {row.enabled ? "Enabled" : "Disabled"}
      </Badge>
    ),
  },
]

const deliveryColumns: Column<DeliveryItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.event.eventType}</code>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Attempts",
    cell: (row) => (
      <span className="text-xs tabular-nums">{row.attempts}/{row.maxAttempts}</span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function DestinationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, reexecute] = useQuery({ query: DestinationQuery, variables: { id: id! } })
  const [, rotateSecret] = useMutation(RotateDestinationSecretMutation)
  const [, regeneratePollKey] = useMutation(RegeneratePollApiKeyMutation)
  const [editOpen, setEditOpen] = useState(false)
  const [rotateOpen, setRotateOpen] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [secretVisible, setSecretVisible] = useState(false)
  const [regenPollKeyOpen, setRegenPollKeyOpen] = useState(false)
  const [regeneratingPollKey, setRegeneratingPollKey] = useState(false)
  const [pollKeyVisible, setPollKeyVisible] = useState(false)
  const [, ackDeliveries] = useMutation(AckDeliveriesMutation)
  const [selectedPollIds, setSelectedPollIds] = useState<Set<string>>(new Set())
  const [acking, setAcking] = useState(false)
  const [pollCursor, setPollCursor] = useState<string | undefined>(undefined)
  const isPoll = result.data?.destination?.deliveryMode === "POLL"

  const [deliveries] = useQuery({
    query: DeliveriesQuery,
    variables: { destinationId: id, limit: 10, offset: 0 },
    pause: isPoll,
  })

  const [pollResult, reexecutePoll] = useQuery({
    query: PollDeliveriesQuery,
    variables: { destinationId: id!, cursor: pollCursor, limit: 25 },
    pause: !isPoll,
  })

  const togglePollId = useCallback((deliveryId: string) => {
    setSelectedPollIds((prev) => {
      const next = new Set(prev)
      if (next.has(deliveryId)) next.delete(deliveryId)
      else next.add(deliveryId)
      return next
    })
  }, [])

  const handleAck = async () => {
    if (selectedPollIds.size === 0) return
    setAcking(true)
    const res = await ackDeliveries({ destinationId: id!, deliveryIds: Array.from(selectedPollIds) })
    setAcking(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Acknowledged ${res.data?.ackDeliveries ?? selectedPollIds.size} deliveries`)
    setSelectedPollIds(new Set())
    reexecutePoll({ requestPolicy: "network-only" })
  }

  const dest = result.data?.destination

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!dest) return <EmptyState title="Destination not found" />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{dest.name}</h1>
          <StatusBadge status={dest.status} />
          <Badge variant={dest.type === "HTTP" ? "secondary" : "outline"} className="text-2xs">
            {destinationTypeLabels[dest.type as DestinationTypeValue] ?? dest.type}
          </Badge>
          {dest.type === "HTTP" && (
            <Badge variant={dest.deliveryMode === "POLL" ? "outline" : "default"} className="text-2xs">
              {dest.deliveryMode}
            </Badge>
          )}
          {dest.ordered && (
            <Badge variant="outline" className="text-2xs">Ordered</Badge>
          )}
          {dest.disabledReason && (
            <Badge variant="destructive" className="text-2xs">
              {dest.disabledReason === "unhealthy" ? "Auto-disabled: unhealthy" : `Disabled: ${dest.disabledReason}`}
            </Badge>
          )}
          {(() => {
            const score = dest.healthScore
            const color = score >= 0.8 ? "bg-emerald-500" : score >= 0.4 ? "bg-amber-500" : "bg-red-500"
            return (
              <div className="flex items-center gap-1.5">
                <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
                <span className="text-xs tabular-nums">{Math.round(score * 100)}%</span>
              </div>
            )
          })()}
        </div>
        <div className="flex items-center gap-2">
          <ShareLinkDialog scopeType="destination" scopeId={id!} />
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Edit
          </Button>
        </div>
      </div>

      {dest.ordered && dest.blockedDelivery && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Ordered delivery is blocked</p>
            <p className="text-xs text-muted-foreground">
              Delivery{" "}
              <Link
                to={`/deliveries/${dest.blockedDelivery.id}`}
                className="font-mono hover:underline text-foreground"
              >
                {dest.blockedDelivery.id.slice(0, 8)}
              </Link>
              {" "}for event type{" "}
              <code className="font-mono">{dest.blockedDelivery.event.eventType}</code>
              {" "}has failed after {dest.blockedDelivery.attempts}/{dest.blockedDelivery.maxAttempts} attempts.
              Subsequent deliveries are paused. Replay or skip the DLQ entry to unblock.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Type</FieldLabel>
                <Badge variant={dest.type === "HTTP" ? "secondary" : "outline"} className="text-2xs">
                  {destinationTypeLabels[dest.type as DestinationTypeValue] ?? dest.type}
                </Badge>
              </div>
              {dest.type === "HTTP" && (
                <div>
                  <FieldLabel className="mb-1">Delivery Mode</FieldLabel>
                  <Badge variant={dest.deliveryMode === "POLL" ? "outline" : "secondary"} className="text-2xs">
                    {dest.deliveryMode}
                  </Badge>
                </div>
              )}
              {dest.type === "HTTP" && dest.deliveryMode !== "POLL" && (
                <div>
                  <FieldLabel className="mb-1">URL</FieldLabel>
                  <div className="flex items-center gap-1">
                    <code className="font-mono text-xs truncate">{dest.url}</code>
                    <CopyButton text={dest.url} />
                  </div>
                </div>
              )}
              {dest.type === "HTTP" && dest.deliveryMode !== "POLL" && (
                <div>
                  <FieldLabel className="mb-1">Timeout</FieldLabel>
                  <div className="tabular-nums">{dest.timeoutMs}ms</div>
                </div>
              )}
              <div>
                <FieldLabel className="mb-1">Rate Limit</FieldLabel>
                <div>{dest.rateLimitRps > 0 ? `${dest.rateLimitRps} rps` : "Unlimited"}</div>
              </div>
              {dest.authType && dest.authType !== "NONE" && (
                <div>
                  <FieldLabel className="mb-1">Auth Type</FieldLabel>
                  <Badge variant="outline" className="text-2xs">{dest.authType}</Badge>
                </div>
              )}
              {dest.outputFormat && (
                <div>
                  <FieldLabel className="mb-1">Output Format</FieldLabel>
                  <div className="font-mono text-xs">{dest.outputFormat}</div>
                </div>
              )}
              {dest.connectorType && (
                <div>
                  <FieldLabel className="mb-1">Connector</FieldLabel>
                  <div className="font-mono text-xs">{dest.connectorType}</div>
                </div>
              )}
              {dest.autoDeletePayload && (
                <div>
                  <FieldLabel className="mb-1">Auto-Delete Payload</FieldLabel>
                  <Badge variant="default" className="text-2xs">Enabled</Badge>
                </div>
              )}
            </div>
            {dest.authType === "OAUTH2" && dest.oauth2Config && (
              <div>
                <FieldLabel className="mb-2">OAuth2 Config</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Token URL</div>
                      <div className="font-medium font-mono truncate">{dest.oauth2Config.tokenUrl}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Client ID</div>
                      <div className="font-medium font-mono truncate">{dest.oauth2Config.clientId}</div>
                    </div>
                    {dest.oauth2Config.scopes && (
                      <div>
                        <div className="text-muted-foreground">Scopes</div>
                        <div className="font-medium font-mono">{dest.oauth2Config.scopes}</div>
                      </div>
                    )}
                    {dest.oauth2Config.audience && (
                      <div>
                        <div className="text-muted-foreground">Audience</div>
                        <div className="font-medium font-mono truncate">{dest.oauth2Config.audience}</div>
                      </div>
                    )}
                  </div>
                </GlassCard>
              </div>
            )}
            {dest.authType === "MTLS" && (dest.mtlsCert || dest.mtlsKey) && (
              <div>
                <FieldLabel className="mb-2">mTLS Certificates</FieldLabel>
                <GlassCard className="p-3 space-y-2">
                  {dest.mtlsCert && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Client Certificate</div>
                      <code className="text-2xs font-mono block truncate">{dest.mtlsCert.slice(0, 60)}...</code>
                    </div>
                  )}
                  {dest.mtlsKey && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Client Key</div>
                      <code className="text-2xs font-mono block truncate">{dest.mtlsKey.slice(0, 60)}...</code>
                    </div>
                  )}
                </GlassCard>
              </div>
            )}
            {dest.type !== "HTTP" && dest.typeConfig && (
              <div>
                <FieldLabel className="mb-2">{destinationTypeLabels[dest.type as DestinationTypeValue]} Config</FieldLabel>
                <JsonViewer data={dest.typeConfig} collapsed />
              </div>
            )}
            {dest.retryPolicy && (
              <div>
                <FieldLabel className="mb-2">Retry Policy</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Max Attempts</div>
                      <div className="font-medium tabular-nums">{dest.retryPolicy.maxAttempts}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Initial Delay</div>
                      <div className="font-medium font-mono">{dest.retryPolicy.initialDelay}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Max Delay</div>
                      <div className="font-medium font-mono">{dest.retryPolicy.maxDelay}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Backoff</div>
                      <div className="font-medium tabular-nums">{dest.retryPolicy.backoffFactor}x</div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
            {dest.headers && (
              <div>
                <FieldLabel className="mb-2">Custom Headers</FieldLabel>
                <JsonViewer data={dest.headers} collapsed />
              </div>
            )}
            {dest.healthConfig && (
              <div>
                <FieldLabel className="mb-2">Health Monitoring</FieldLabel>
                <GlassCard className="p-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Window</div>
                      <div className="font-medium tabular-nums">{dest.healthConfig.windowHours}h</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Disable Below</div>
                      <div className="font-medium tabular-nums">{Math.round(dest.healthConfig.disableBelow * 100)}%</div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{dest.id}</code>
                <CopyButton text={dest.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Signing Secret</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{"whsec_" + "•".repeat(20)}</code>
                <button
                  type="button"
                  className="text-2xs text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setSecretVisible(true)}
                >
                  Show
                </button>
                <CopyButton text={dest.signingSecret} />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setRotateOpen(true)}
              >
                <RefreshCw className="h-3 w-3 mr-1.5" />
                Rotate
              </Button>
            </div>
            {dest.deliveryMode === "POLL" && (
              <div>
                <FieldLabel className="mb-1">Poll API Key</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs">
                    {dest.pollApiKeyPrefix ? `${dest.pollApiKeyPrefix}${"•".repeat(16)}` : "Not generated"}
                  </code>
                  {dest.pollApiKey && (
                    <>
                      <button
                        type="button"
                        className="text-2xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setPollKeyVisible(true)}
                      >
                        Show
                      </button>
                      <CopyButton text={dest.pollApiKey} />
                    </>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => setRegenPollKeyOpen(true)}
                >
                  <RefreshCw className="h-3 w-3 mr-1.5" />
                  Regenerate
                </Button>
              </div>
            )}
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={dest.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      {dest.metrics && (
        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Performance (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-xs text-muted-foreground">P50</div>
                <div className="text-lg font-semibold tabular-nums">{dest.metrics.p50}ms</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground">P95</div>
                <div className="text-lg font-semibold tabular-nums">{dest.metrics.p95}ms</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground">P99</div>
                <div className="text-lg font-semibold tabular-nums">{dest.metrics.p99}ms</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-muted-foreground">Max</div>
                <div className="text-lg font-semibold tabular-nums">{dest.metrics.max}ms</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Subscriptions</h2>
        <DataTable
          columns={subColumns}
          data={dest.subscriptions}
          keyFn={(row) => row.id}
          emptyTitle="No subscriptions"
        />
      </div>

      {dest.deliveryMode === "POLL" ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Poll Queue</h2>
            <div className="flex items-center gap-2">
              {selectedPollIds.size > 0 && (
                <Button
                  size="sm"
                  onClick={handleAck}
                  disabled={acking}
                >
                  {acking ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Acknowledge ({selectedPollIds.size})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => reexecutePoll({ requestPolicy: "network-only" })}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Refresh
              </Button>
            </div>
          </div>
          <DataTable<PollDeliveryItem>
            columns={[
              {
                header: "",
                cell: (row) => (
                  <Checkbox
                    checked={selectedPollIds.has(row.id)}
                    onCheckedChange={() => togglePollId(row.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                ),
                className: "w-10",
              },
              {
                header: "Event Type",
                cell: (row) => <code className="text-xs font-mono">{row.event.eventType}</code>,
              },
              {
                header: "Status",
                cell: (row) => <StatusBadge status={row.status} />,
              },
              {
                header: "Attempts",
                cell: (row) => (
                  <span className="text-xs tabular-nums">{row.attempts}/{row.maxAttempts}</span>
                ),
              },
              {
                header: "Created",
                cell: (row) => <TimeAgo date={row.createdAt} />,
              },
            ]}
            data={pollResult.data?.pollDeliveries.nodes ?? []}
            keyFn={(row) => row.id}
            loading={pollResult.fetching}
            emptyTitle="No pending deliveries"
            emptyDescription="The poll queue is empty"
            onRowClick={(row) => navigate(`/deliveries/${row.id}`)}
          />
          {pollResult.data?.pollDeliveries.pageInfo.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPollCursor(pollResult.data?.pollDeliveries.pageInfo.endCursor ?? undefined)}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-sm font-medium">Recent Deliveries</h2>
          <DataTable
            columns={deliveryColumns}
            data={deliveries.data?.deliveries.nodes ?? []}
            keyFn={(row) => row.id}
            loading={deliveries.fetching}
            emptyTitle="No deliveries"
            onRowClick={(row) => navigate(`/deliveries/${row.id}`)}
          />
        </div>
      )}

      <DestinationForm
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
        initialData={dest}
      />

      <Dialog open={secretVisible} onOpenChange={setSecretVisible}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Signing Secret</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <GlassCard className="p-3">
              <code className="font-mono text-xs break-all select-all">{dest.signingSecret}</code>
            </GlassCard>
            <div className="flex justify-end">
              <CopyButton text={dest.signingSecret} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={rotateOpen}
        onOpenChange={setRotateOpen}
        title="Rotate Signing Secret"
        description="The current secret will become the secondary secret for key rotation. Recipients should verify against both secrets during the transition."
        confirmLabel="Rotate"
        onConfirm={async () => {
          setRotating(true)
          const res = await rotateSecret({ id: id! })
          setRotating(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Signing secret rotated")
          setRotateOpen(false)
          setSecretVisible(true)
          reexecute({ requestPolicy: "network-only" })
        }}
        loading={rotating}
      />

      {dest.deliveryMode === "POLL" && dest.pollApiKey && (
        <Dialog open={pollKeyVisible} onOpenChange={setPollKeyVisible}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Poll API Key</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <GlassCard className="p-3">
                <code className="font-mono text-xs break-all select-all">{dest.pollApiKey}</code>
              </GlassCard>
              <div className="flex justify-end">
                <CopyButton text={dest.pollApiKey} />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ConfirmDialog
        open={regenPollKeyOpen}
        onOpenChange={setRegenPollKeyOpen}
        title="Regenerate Poll API Key"
        description="The current poll API key will be invalidated immediately. Any consumers using the current key will lose access."
        confirmLabel="Regenerate"
        onConfirm={async () => {
          setRegeneratingPollKey(true)
          const res = await regeneratePollKey({ destinationId: id! })
          setRegeneratingPollKey(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Poll API key regenerated")
          setRegenPollKeyOpen(false)
          setPollKeyVisible(true)
          reexecute({ requestPolicy: "network-only" })
        }}
        loading={regeneratingPollKey}
      />
    </div>
  )
}
